// Security fix run 2, Part 2 (LB-402 / SR2-09): ONE way for this browser to forget a household.
//
// "Leave household" used to remove the code and the offline cache and nothing else: the device
// token (lb_device_token_CODE), the proof, the role and creator keys and the push registration
// all stayed, and no request told the worker the device had gone, so its registry row stayed
// live and proven. Root.forceLeaveRevoked and Root.forceUnlinkDeleted each did a slightly
// different subset. Three teardowns that drift apart is how a credential survives a sign-out;
// this is the one teardown, and the three callers share it.
//
// What it clears: every localStorage key the inventory's "Storage keys" section lists under a
// household code — named below where this module can import the constant, and swept by suffix
// for the rest (lb_push_reg_CODE and lb_push_news_CODE live in components/onboarding/push.jsx;
// the sweep is what makes a key added later forgotten too) — plus the two sessionStorage keys
// (the Parent Mode grace stamp and the active adult). The device id (tq_device_id) is NOT
// touched: it names this browser, not the household, and handleJoin regenerates it on the
// next join anyway (a revoked id must never be reused; see regenerateDeviceId).
//
// `revoke`: Leave also asks the worker to remove THIS device's own row first (the existing
// POST /devices/:id/remove, which requireDevice gates and which any member may call for
// itself). A failure is ignored — a dead network must not keep a person signed in — and the
// local clear runs regardless, so the browser never keeps a credential the server still
// honours. Revocation and deletion callers pass nothing: the server has already acted.
import { ACTIVE_CHILD_KEY_PREFIX, CREATOR_KEY_PREFIX, DEVICE_CODE_KEY, DEVICE_ROLE_KEY_PREFIX, HIDDEN_CHILDREN_KEY_PREFIX, MUTED_CHILDREN_KEY_PREFIX, PARENT_TAB_KEY_PREFIX, PENDING_DEVICE_SETUP_KEY_PREFIX, REQUESTS_OVERLAY_SHOWN_KEY_PREFIX, SEEN_CHILDREN_KEY_PREFIX } from "./constants.js";
import { ACTIVE_ADULT_KEY_PREFIX, DEVICE_OWNER_KEY_PREFIX, DEVICE_PROOF_KEY_PREFIX, DEVICE_TOKEN_KEY_PREFIX, HOUSEHOLD_CACHE_PREFIX, PARENT_GRACE_KEY_PREFIX, apiRemoveDevice, getDeviceId } from "./api.js";
import { HW_HELD_LIST_KEY_PREFIX } from "./homework-share.js";

export const HOUSEHOLD_LOCAL_PREFIXES = [
  ACTIVE_CHILD_KEY_PREFIX, CREATOR_KEY_PREFIX, DEVICE_ROLE_KEY_PREFIX, HIDDEN_CHILDREN_KEY_PREFIX, MUTED_CHILDREN_KEY_PREFIX,
  PARENT_TAB_KEY_PREFIX, PENDING_DEVICE_SETUP_KEY_PREFIX, REQUESTS_OVERLAY_SHOWN_KEY_PREFIX, SEEN_CHILDREN_KEY_PREFIX,
  DEVICE_OWNER_KEY_PREFIX, DEVICE_PROOF_KEY_PREFIX, DEVICE_TOKEN_KEY_PREFIX, HOUSEHOLD_CACHE_PREFIX, HW_HELD_LIST_KEY_PREFIX,
];
export const HOUSEHOLD_SESSION_PREFIXES = [PARENT_GRACE_KEY_PREFIX, ACTIVE_ADULT_KEY_PREFIX];

// Every key in `store` that names this household: the listed prefixes, and any other app key
// (tq_* / lb_* / lb-*) carrying the code as a segment — at the end (lb_push_reg_CODE) or in the
// middle (the per-child lb_decided_CODE_c1 and lb_celebrated_CODE_c1 in AskToBuy and
// ChildView, which the inventory does not list because they are template strings). The guest
// practice results (lb_hw_tried:HW-…) carry a list code, not a household code, and are left
// alone by the colon rule.
function keysNaming(store, code, prefixes) {
  const out = new Set();
  for (const p of prefixes) out.add(p + code);
  const segment = new RegExp(`[_-]${code}(?:$|[_-])`);
  try {
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (!k || k.includes(":")) continue;
      if (!/^(tq|lb)[_-]/.test(k)) continue;
      if (segment.test(k)) out.add(k);
    }
  } catch { /* a store without key()/length (the bare-Node guard jar): the listed prefixes suffice */ }
  return [...out];
}

export function clearHouseholdLocal(code) {
  const c = String(code || "").toUpperCase();
  if (!c) return;
  try { for (const k of keysNaming(localStorage, c, HOUSEHOLD_LOCAL_PREFIXES)) localStorage.removeItem(k); } catch {}
  try { for (const k of keysNaming(sessionStorage, c, HOUSEHOLD_SESSION_PREFIXES)) sessionStorage.removeItem(k); } catch {}
  try { if ((localStorage.getItem(DEVICE_CODE_KEY) || "").toUpperCase() === c) localStorage.removeItem(DEVICE_CODE_KEY); } catch {}
}

export const LEAVE_REVOKE_TIMEOUT_MS = 4000;

export async function forgetHousehold(code, { revoke = false } = {}) {
  const c = String(code || "").toUpperCase();
  if (!c) return;
  if (revoke) {
    // Best effort, bounded: the person tapped Leave and is leaving whatever the network says.
    try {
      await Promise.race([
        apiRemoveDevice(c, getDeviceId()),
        new Promise((resolve) => setTimeout(resolve, LEAVE_REVOKE_TIMEOUT_MS)),
      ]);
    } catch { /* already revoked, offline, or refused: the local clear below is what matters */ }
  }
  clearHouseholdLocal(c);
}
